import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { HeartPulse, CheckCircle } from 'lucide-react';
export default function TraumaPTSD() {
  return <main className="min-h-screen bg-white" data-id="element-1412">
      <Helmet data-id="element-1413">
        <title data-id="element-1414">PTSD Medico-Legal Report | Trauma Psychiatric Assessment</title>
        <meta name="description" content="Expert PTSD and trauma psychiatric evaluations for personal injury, asylum, and legal proceedings. Istanbul Protocol compliant reports by Dr Imtiaz Syed." data-id="element-1415" />
        <meta name="keywords" content="PTSD & Trauma Psychiatric Evaluations, PTSD Evaluations, Trauma Assessment, Complex PTSD, Istanbul Protocol, Psychological Injury, Expert Witness Psychiatrist, Independent PTSD assessment for asylum cases UK" data-id="element-1416" />
        <script type="application/ld+json" data-id="element-1417">
          {`
            {
              "@context": "https://schema.org",
              "@type": "MedicalService",
              "name": "PTSD & Trauma Psychiatric Evaluations",
              "provider": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "serviceType": "PTSD & Trauma Evaluation",
              "audience": {
                "@type": "Audience",
                "name": "Legal Professionals"
              },
              "availableChannel": {
                "@type": "ServiceChannel",
                "name": "Online and In-person",
                "serviceUrl": "https://medicolegal-psychiatry.co.uk/contact",
                "servicePhone": "",
                "serviceSmsNumber": ""
              },
              "serviceOutput": "Expert PTSD & Trauma Reports for Legal Proceedings"
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-16 pt-24 md:pt-28" data-id="element-1418">
        <div className="container mx-auto px-4" data-id="element-1419">
          <div className="flex items-center justify-center mb-6" data-id="element-1420">
            <HeartPulse className="h-16 w-16 mr-4" data-id="element-1421" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-1422">
              PTSD & Trauma Psychiatric Evaluations
            </h1>
          </div>
          <p className="text-xl max-w-3xl mx-auto text-center" data-id="element-1423">
            Expert Insights into Psychological Injury
          </p>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-16" data-id="element-1424">
        <div className="container mx-auto px-4" data-id="element-1425">
          <div className="max-w-3xl mx-auto" data-id="element-1426">
            <p className="text-slate-dark text-lg mb-6" data-id="element-1427">
              Understanding the profound impact of trauma is crucial in many
              legal contexts. Dr. Imtiaz Syed provides meticulous and sensitive
              assessments for Post-Traumatic Stress Disorder (PTSD) and Complex
              PTSD, offering clear and robust expert opinions for legal
              proceedings. His evaluations help illuminate the psychological
              consequences of traumatic events, ensuring these are accurately
              presented and understood within the legal framework.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1428">
              Why Instruct Dr. Imtiaz Syed for Trauma & PTSD Evaluations?
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1429">
              Dr. Syed's expertise in trauma-informed psychiatry ensures:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-1430">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1431">
                <div className="flex items-start" data-id="element-1432">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1433" />
                  <div data-id="element-1434">
                    <h3 className="font-bold text-primary" data-id="element-1435">
                      Sensitive & Thorough Assessments
                    </h3>
                    <p className="text-slate" data-id="element-1436">
                      Conducted with deep understanding and respect for the
                      individual's experience.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1437">
                <div className="flex items-start" data-id="element-1438">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1439" />
                  <div data-id="element-1440">
                    <h3 className="font-bold text-primary" data-id="element-1441">
                      Istanbul Protocol Compliance
                    </h3>
                    <p className="text-slate" data-id="element-1442">
                      Reports for international contexts (e.g., asylum) often
                      adhere to these rigorous standards.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1443">
                <div className="flex items-start" data-id="element-1444">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1445" />
                  <div data-id="element-1446">
                    <h3 className="font-bold text-primary" data-id="element-1447">
                      Clarifying Complex Trauma
                    </h3>
                    <p className="text-slate" data-id="element-1448">
                      Expertise in assessing conditions arising from torture,
                      gender-based violence (GBV), modern slavery, and other
                      severe experiences.
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1449">
                <div className="flex items-start" data-id="element-1450">
                  <CheckCircle className="h-5 w-5 text-secondary mr-2 mt-1" data-id="element-1451" />
                  <div data-id="element-1452">
                    <h3 className="font-bold text-primary" data-id="element-1453">
                      Objective & Robust Reports
                    </h3>
                    <p className="text-slate" data-id="element-1454">
                      Providing impartial, evidence-based findings that clearly
                      outline diagnosis, causation, and prognosis where
                      relevant.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1455">
              Key Trauma & PTSD Assessments Provided
            </h2>
            <p className="text-slate-dark mb-4" data-id="element-1456">
              Dr. Syed offers expert evaluations for:
            </p>
            <ul className="space-y-3 mb-8 text-slate-dark" data-id="element-1457">
              <li className="flex items-start" data-id="element-1458">
                <span className="text-secondary font-bold mr-2" data-id="element-1459">•</span>
                <span data-id="element-1460">
                  <strong data-id="element-1461">Post-Traumatic Stress Disorder (PTSD):</strong>{' '}
                  Diagnostic assessments for the classic presentation of PTSD.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1462">
                <span className="text-secondary font-bold mr-2" data-id="element-1463">•</span>
                <span data-id="element-1464">
                  <strong data-id="element-1465">Complex PTSD:</strong> Evaluations for prolonged,
                  repeated, or multiple forms of trauma.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1466">
                <span className="text-secondary font-bold mr-2" data-id="element-1467">•</span>
                <span data-id="element-1468">
                  <strong data-id="element-1469">Trauma-Related Psychological Injuries:</strong>{' '}
                  Assessments in personal injury claims where trauma has led to
                  psychological distress.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1470">
                <span className="text-secondary font-bold mr-2" data-id="element-1471">•</span>
                <span data-id="element-1472">
                  <strong data-id="element-1473">Impact of Specific Traumatic Events:</strong> Expert
                  opinions on the psychological effects of accidents, abuse, or
                  other significant incidents.
                </span>
              </li>
            </ul>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1474">
              Our Seamless Process & Robust Reports
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1475">
              Our assessment process is thorough and efficient, involving
              careful review of documentation and sensitive clinical interviews.
              Dr. Syed offers both secure remote assessments across the UK and
              in-person consultations when appropriate. The resulting reports
              are always clear, legally relevant, and provide precise findings
              and actionable insights for your case, designed to withstand
              scrutiny.
            </p>
            <h2 className="text-2xl font-serif font-bold text-primary mb-4" data-id="element-1476">
              Partnering for Just Outcomes
            </h2>
            <p className="text-slate-dark mb-6" data-id="element-1477">
              Dr. Imtiaz Syed is your trusted partner in navigating the
              complexities of trauma-related claims. His expert evaluations
              provide the clarity and depth required to ensure psychological
              injuries are understood and justly addressed within the legal
              system.
            </p>

            {/* Related Services */}
            <div className="mb-10 border-t border-gray-200 pt-8" data-id="element-1478">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1479">
                Explore Our Other Services
              </h3>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4" data-id="element-1480">
                <Link to="/services/specialist" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1481">
                  Psychiatric Reports for Courts & Tribunals
                </Link>
                <Link to="/services/clinical-negligence" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1482">
                  Clinical Negligence Cases
                </Link>
                <Link to="/services/forensic-risk" className="text-secondary hover:text-primary font-medium underline transition-colors" data-id="element-1483">
                  Forensic & Risk Assessments
                </Link>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-md mb-8" data-id="element-1484">
              <h3 className="text-xl font-serif font-bold text-primary mb-4" data-id="element-1485">
                Ready to Instruct Dr. Syed?
              </h3>
              <p className="text-slate-dark mb-4" data-id="element-1486">
                We invite you to discuss your specific case requirements.
              </p>
              <div className="flex items-center" data-id="element-1487">
                <span className="mr-2 text-slate-dark" data-id="element-1488">Contact Us:</span>
                <a href="mailto:enquiries@medicolegal-psychiatry.co.uk" className="text-primary hover:text-secondary transition-colors" data-id="element-1489">
                  enquiries@medicolegal-psychiatry.co.uk
                </a>
              </div>
            </div>
            <div className="text-center" data-id="element-1490">
              <Button variant="primary" size="lg" href="/contact" data-id="element-1491">
                Book a PTSD Assessment
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>;
}